import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];
  
  /**
   * ResizeObserver for height adjustments
   * @type {ResizeObserver | null}
   */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    
    // Add resize listener for height adjustments
    this.#resizeObserver = new ResizeObserver(() => {
      if (this.isOpen) {
        this.#adjustMenuHeight();
      }
    });
    
    this.#resizeObserver.observe(document.body);
    
    // Auto-close menu on page load
    this.#setupAutoClose();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#resizeObserver?.disconnect();
  }

  /**
   * Setup auto-close functionality for page navigation
   */
  #setupAutoClose() {
    // Close menu when page loads (handles browser back/forward navigation)
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.#closeOnPageLoad();
      });
    } else {
      this.#closeOnPageLoad();
    }
    
    // Close menu when navigating away (for single-page app scenarios)
    window.addEventListener('beforeunload', () => {
      if (this.isOpen) {
        this.#closeImmediately();
      }
    });
    
    // Close menu on popstate (browser back/forward buttons)
    window.addEventListener('popstate', () => {
      if (this.isOpen) {
        this.close();
      }
    });
  }

  /**
   * Close menu on page load if it's open
   */
  #closeOnPageLoad() {
    // Small delay to ensure DOM is fully ready
    setTimeout(() => {
      if (this.isOpen) {
        this.#closeImmediately();
      }
    }, 100);
  }

  /**
   * Immediately close the menu without animations (for page transitions)
   */
  #closeImmediately() {
    const summary = this.refs.details.querySelector('summary');
    
    if (!summary) return;

    // Remove classes and attributes immediately
    summary.setAttribute('aria-expanded', 'false');
    this.refs.details.classList.remove('menu-open');
    this.refs.details.removeAttribute('open');
    
    // Remove focus trap
    removeTrapFocus();
    
    // Close any open submenus
    const openDetails = this.querySelectorAll('details[open]');
    openDetails.forEach(detail => {
      detail.classList.remove('menu-open');
      detail.removeAttribute('open');
      const detailSummary = detail.querySelector('summary');
      if (detailSummary) {
        detailSummary.setAttribute('aria-expanded', 'false');
      }
    });
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get animation type from data attribute
   * @returns {string} Animation type: 'slide' or 'dropdown'
   */
  #getAnimationType() {
    return this.dataset.animationType || 'slide';
  }

  /**
   * Get height mode from data attribute
   * @returns {string} Height mode: 'fit' or 'full'
   */
  #getHeightMode() {
    return this.dataset.heightMode || 'fit';
  }

  /**
   * Adjust menu positioning based on height mode
   */
  #adjustMenuHeight() {
    const heightMode = this.#getHeightMode();
    const animationType = this.#getAnimationType();
    const menuDrawer = this.querySelector('.menu-drawer');
    
    if (!menuDrawer) return;
    
    if (heightMode === 'fit' && animationType === 'dropdown') {
      // For fit + dropdown, calculate optimal height
      const viewportHeight = window.innerHeight;
      const headerHeight = document.querySelector('header')?.offsetHeight || 0;
      const maxHeight = Math.min(viewportHeight * 0.8, viewportHeight - headerHeight - 40);
      
      menuDrawer.style.setProperty('--dropdown-max-height', `${maxHeight}px`);
    }
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    
    // Adjust height before opening
    this.#adjustMenuHeight();
    
    // Different behavior based on animation type
    const animationType = this.#getAnimationType();
    
    if (animationType === 'dropdown') {
      requestAnimationFrame(() => {
        details.classList.add('menu-open');
        const menuDrawer = details.querySelector('.menu-drawer');
        if (menuDrawer) {
          menuDrawer.scrollTop = 0;
        }
      });
    } else {
      requestAnimationFrame(() => details.classList.add('menu-open'));
    }

    trapFocus(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
